import type { OutboxEntry } from '@/domain/outbox'
import {
  type AttendanceRecord,
  type LocalData,
  type Meeting,
  type Member,
  type PullChanges,
  type UserPublic,
  attendanceKey,
} from '@cadoan/shared'
import { db } from './db'

/**
 * The only module that touches Dexie.
 *
 * Data writes are fire-and-forget (the UI never waits on disk) but never
 * silent: each goes through `track`, which reports a failure to the store
 * (shown as a toast) and keeps the in-flight set so the sync engine and tests
 * can `flushWrites()` before reading.
 */

export interface WriteFailure {
  op: string
  message: string
}

const pending = new Set<Promise<void>>()
let failureHandler: ((f: WriteFailure) => void) | null = null

export function onWriteFailure(fn: ((f: WriteFailure) => void) | null): void {
  failureHandler = fn
}

function track(op: string, work: Promise<unknown>): void {
  const settled = work
    .then(() => undefined)
    .catch((err: unknown) => {
      failureHandler?.({ op, message: err instanceof Error ? err.message : String(err) })
    })
    .finally(() => pending.delete(settled))
  pending.add(settled)
}

export async function flushWrites(): Promise<void> {
  while (pending.size > 0) await Promise.all([...pending])
}

// ── Session and small values ─────────────────────────────────────────────────

export interface StoredSession {
  token: string
  user: UserPublic
}

async function getKv<T>(key: string): Promise<T | undefined> {
  return (await db.kv.get(key))?.value as T | undefined
}

function setKv(key: string, value: unknown): void {
  track(`kv.${key}`, db.kv.put({ key, value }))
}

export const loadSession = () => getKv<StoredSession>('session')
export const saveSession = (s: StoredSession) => setKv('session', s)
export const saveCursor = (rev: number) => setKv('cursor', rev)
export const saveLastSyncAt = (at: number) => setKv('lastSyncAt', at)

// ── Data ─────────────────────────────────────────────────────────────────────

export interface Loaded {
  session: StoredSession | undefined
  data: LocalData
  outbox: OutboxEntry[]
  cursor: number
  lastSyncAt: number | null
}

export async function loadAll(): Promise<Loaded> {
  const [session, members, meetings, attendance, reasons, people, outbox, cursor, lastSyncAt] =
    await Promise.all([
      loadSession(),
      db.members.toArray(),
      db.meetings.toArray(),
      db.attendance.toArray(),
      db.reasons.toArray(),
      db.people.toArray(),
      db.outbox.toArray(),
      getKv<number>('cursor'),
      getKv<number>('lastSyncAt'),
    ])
  return {
    session,
    data: {
      members: new Map(members.map((m) => [m.id, m])),
      meetings: new Map(meetings.map((m) => [m.id, m])),
      attendance: new Map(attendance.map(({ key, ...r }) => [key, r as AttendanceRecord] as const)),
      reasons: new Map(reasons.map((r) => [r.id, r])),
      people: new Map(people.map((p) => [p.id, p.displayName])),
    },
    outbox,
    cursor: cursor ?? 0,
    lastSyncAt: lastSyncAt ?? null,
  }
}

/** A local mark plus its outbox entry, written together. */
export function saveMark(record: AttendanceRecord, entry: OutboxEntry): void {
  const key = attendanceKey(record.meetingId, record.memberId)
  track(
    'mark',
    db.transaction('rw', db.attendance, db.outbox, async () => {
      await db.attendance.put({ ...record, key })
      await db.outbox.put(entry)
    }),
  )
}

export function saveMarks(records: AttendanceRecord[], entries: OutboxEntry[]): void {
  track(
    'marks',
    db.transaction('rw', db.attendance, db.outbox, async () => {
      await db.attendance.bulkPut(
        records.map((r) => ({ ...r, key: attendanceKey(r.meetingId, r.memberId) })),
      )
      await db.outbox.bulkPut(entries)
    }),
  )
}

export function saveMeeting(meeting: Meeting, entry: OutboxEntry): void {
  track(
    'meeting',
    db.transaction('rw', db.meetings, db.outbox, async () => {
      await db.meetings.put(meeting)
      await db.outbox.put(entry)
    }),
  )
}

/** A member as the server returned it after an online edit. */
export function saveMember(member: Member): void {
  track('member', db.members.put(member))
}

export function deleteLocalAttendance(keys: string[]): void {
  if (keys.length) track('attendance.delete', db.attendance.bulkDelete(keys))
}

export function deleteLocalMeeting(id: string): void {
  track(
    'meeting.delete',
    db.transaction('rw', db.meetings, db.attendance, async () => {
      await db.meetings.delete(id)
      await db.attendance.where('meetingId').equals(id).delete()
    }),
  )
}

export function applyChanges(c: PullChanges): void {
  track(
    'pull',
    db.transaction(
      'rw',
      [db.members, db.meetings, db.attendance, db.reasons, db.people],
      async () => {
        await db.members.bulkPut(c.putMembers)
        await db.meetings.bulkPut(c.putMeetings)
        await db.meetings.bulkDelete(c.deleteMeetings)
        await db.attendance.bulkDelete(c.deleteAttendance)
        await db.attendance.bulkPut(
          c.putAttendance.map((r) => ({ ...r, key: attendanceKey(r.meetingId, r.memberId) })),
        )
        await db.reasons.bulkPut(c.putReasons)
        await db.reasons.bulkDelete(c.deleteReasons)
        if (c.people.length) {
          await db.people.clear()
          await db.people.bulkPut(c.people)
        }
      },
    ),
  )
}

// ── Outbox ───────────────────────────────────────────────────────────────────

export function outboxAll(): Promise<OutboxEntry[]> {
  return db.outbox.toArray()
}

/**
 * Drops entries the API has answered — but only if they were not replaced by a
 * newer write while the request was in flight. A replaced entry carries a
 * different stamp and stays queued for the next round.
 */
export async function removeSent(sent: OutboxEntry[]): Promise<void> {
  await db.transaction('rw', db.outbox, async () => {
    for (const e of sent) {
      const current = await db.outbox.get(e.key)
      if (current && current.op.at === e.op.at) await db.outbox.delete(e.key)
    }
  })
}

/** Sign-out: nothing of the previous account stays on the phone. */
export async function clearAll(): Promise<void> {
  await flushWrites()
  await Promise.all(db.tables.map((t) => t.clear()))
}
